/**
 * Custom hook for detecting online/offline connectivity status.
 *
 * Uses navigator.onLine and online/offline event listeners to track
 * the current connectivity state of the browser.
 */
import { useState, useEffect, useCallback, useRef } from 'react';

interface UseOnlineStatusResult {
  /** Whether the browser is currently online */
  isOnline: boolean;
  /** Register a callback to be invoked once when connectivity is restored */
  onReconnect: (callback: () => void) => void;
}

export function useOnlineStatus(): UseOnlineStatusResult {
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  const reconnectCallbackRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Invoke the registered reconnect callback
      if (reconnectCallbackRef.current) {
        reconnectCallbackRef.current();
        reconnectCallbackRef.current = null;
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const onReconnect = useCallback((callback: () => void) => {
    reconnectCallbackRef.current = callback;
  }, []);

  return { isOnline, onReconnect };
}
